import { create } from 'zustand';
import { CelestialBody } from '../data/celestialData';

export interface MissionProgress {
  exploredBodiesInMission: string[];
  compareBodies: string[];
  observedEvents: string[];
}

export const MAX_ACTIVE_MISSIONS = 3;

function getInitialKnowledgeCount(): { bronze: number; silver: number; gold: number } {
  try {
    const bronze = Object.keys(localStorage).filter(
      (k) => k.startsWith('visited-') && k.endsWith('-bronze')
    ).length;
    const silver = Object.keys(localStorage).filter(
      (k) => k.startsWith('visited-') && k.endsWith('-silver')
    ).length;
    const gold = Object.keys(localStorage).filter(
      (k) => k.startsWith('visited-') && k.endsWith('-gold')
    ).length;
    return { bronze, silver, gold };
  } catch {
    return { bronze: 0, silver: 0, gold: 0 };
  }
}

function getInitialUnlockedAchievements(): string[] {
  try {
    const stored = localStorage.getItem('unlockedAchievements');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export type ViewMode = 'overview' | 'focused';
export type TimeSpeed = 'pause' | '1x' | '10x' | '100x' | '1000x';
export type TimeMode = 'simulation' | 'light-speed' | 'lifetime';
export type JourneyMode = 'idle' | 'running' | 'paused' | 'completed';
export type AppPhase = 'loading' | 'welcome' | 'transition' | 'main';

interface AppState {
  // 当前选中天体
  selectedBody: CelestialBody | null;
  setSelectedBody: (body: CelestialBody | null) => void;

  // 视角模式
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // 时间控制
  timeSpeed: TimeSpeed;
  setTimeSpeed: (speed: TimeSpeed) => void;
  currentDay: number; // 相对于J2000.0的天数
  setCurrentDay: (day: number | ((prev: number) => number)) => void;

  // 时间模式
  timeMode: TimeMode;
  setTimeMode: (mode: TimeMode) => void;

  // 是否显示轨道
  showOrbits: boolean;
  setShowOrbits: (show: boolean) => void;

  // 是否显示标签
  showLabels: boolean;
  setShowLabels: (show: boolean) => void;

  // 相机目标位置（用于动画过渡）
  cameraTarget: [number, number, number] | null;
  setCameraTarget: (target: [number, number, number] | null) => void;
  // 相机注视目标
  cameraLookAt: [number, number, number] | null;
  setCameraLookAt: (target: [number, number, number] | null) => void;
  // 原子化设置相机焦点（避免 cameraTarget/cameraLookAt 分开更新导致渲染不同步）
  setCameraFocus: (target: [number, number, number], lookAt: [number, number, number]) => void;

  // 是否显示知识面板
  showKnowledge: boolean;
  setShowKnowledge: (show: boolean) => void;

  // 重置状态
  resetView: () => void;

  // 行星放大倍率（1x = 真实比例，滑块 1~10）
  planetScale: number;
  setPlanetScale: (scale: number) => void;

  // Achievement system
  unlockedAchievements: string[];
  unlockAchievement: (id: string) => void;
  achievementQueue: string[];
  dequeueAchievement: () => void;
  showAchievementPanel: boolean;
  setShowAchievementPanel: (show: boolean) => void;

  // Exploration tracking
  exploredBodies: string[];
  addExploredBody: (bodyId: string) => void;

  // Mission counters
  completedMissionCount: number;
  incrementMissionCount: () => void;

  // Knowledge counters
  unlockedKnowledgeCount: { bronze: number; silver: number; gold: number };
  incrementKnowledgeCount: (level: 'bronze' | 'silver' | 'gold') => void;

  // Time tracking
  totalTimeAdvanced: number;
  addTimeAdvanced: (days: number) => void;

  // Journey mode
  journeyMode: JourneyMode;
  setJourneyMode: (mode: JourneyMode) => void;
  currentJourneyIndex: number;
  setCurrentJourneyIndex: (index: number) => void;
  showJourneyHUD: boolean;
  setShowJourneyHUD: (show: boolean) => void;

  // Prediction game
  showPredictionGame: boolean;
  setShowPredictionGame: (show: boolean) => void;
  predictionBodyId: string | null;
  setPredictionBodyId: (id: string | null) => void;
  predictionDays: number;
  setPredictionDays: (days: number) => void;
  predictionUserAngle: number;
  setPredictionUserAngle: (angle: number) => void;
  predictionResult: { actualAngle: number; error: number } | null;
  setPredictionResult: (result: { actualAngle: number; error: number } | null) => void;

  // Sandbox experiment
  showSandbox: boolean;
  setShowSandbox: (show: boolean) => void;
  sandboxOrbitAU: number;
  setSandboxOrbitAU: (au: number) => void;

  // Spacecraft panel
  showSpacecraftPanel: boolean;
  setShowSpacecraftPanel: (show: boolean) => void;
  selectedSpacecraft: string | null;
  setSelectedSpacecraft: (id: string | null) => void;

  // Hohmann transfer designer
  showHohmannDesigner: boolean;
  setShowHohmannDesigner: (show: boolean) => void;
  hohmannTarget: string;
  setHohmannTarget: (id: string) => void;

  // Lunar eclipse lab
  showEclipseLab: boolean;
  setShowEclipseLab: (show: boolean) => void;

  // Black hole simulator
  showBlackHole: boolean;
  setShowBlackHole: (show: boolean) => void;

  // Voting
  userVotes: Record<string, string>;
  setUserVote: (controversyId: string, optionId: string) => void;

  // Narrative missions
  showNarrative: boolean;
  setShowNarrative: (show: boolean) => void;
  activeNarrative: string | null;
  setActiveNarrative: (id: string | null) => void;
  narrativeStep: number;
  setNarrativeStep: (step: number) => void;

  // Camera animation guard (prevents OrbitControls conflict)
  isCameraAnimating: boolean;
  setCameraAnimating: (v: boolean) => void;


  // Scientist gallery
  showScientistGallery: boolean;
  setShowScientistGallery: (show: boolean) => void;

  // Scale ruler
  showScaleRuler: boolean;
  setShowScaleRuler: (show: boolean) => void;

  // Star map
  showStarMap: boolean;
  setShowStarMap: (show: boolean) => void;

  // Exploration history
  showExplorationHistory: boolean;
  setShowExplorationHistory: (show: boolean) => void;
  explorationHistorySelectedMilestone: string | null;
  setExplorationHistorySelectedMilestone: (id: string | null) => void;

  // Mission system — 支持最多 3 个同时进行
  activeMissionIds: string[];
  missionProgressMap: Record<string, MissionProgress>;
  hintIndexMap: Record<string, number>;
  addActiveMission: (id: string) => void;
  removeActiveMission: (id: string) => void;
  addMissionExploredBody: (missionId: string, bodyId: string) => void;
  addMissionCompareBody: (missionId: string, bodyId: string) => void;
  addMissionObservedEvent: (missionId: string, eventId: string) => void;
  completedMissions: string[];
  completeMission: (id: string) => void;
  showMissionPanel: boolean;
  setShowMissionPanel: (show: boolean) => void;
  nextHint: (missionId: string) => void;

  // 欢迎页阶段管理
  appPhase: AppPhase;
  setAppPhase: (phase: AppPhase) => void;
  isFirstVisit: boolean;
  markVisited: () => void;
}

export const useStore = create<AppState>((set) => ({
  selectedBody: null,
  setSelectedBody: (body) => set({ selectedBody: body, showKnowledge: !!body }),

  viewMode: 'overview',
  setViewMode: (mode) => set({ viewMode: mode }),

  timeSpeed: '1x',
  setTimeSpeed: (speed) => set({ timeSpeed: speed }),
  timeMode: 'simulation',
  setTimeMode: (mode) => set({ timeMode: mode }),
  currentDay: 0,
  setCurrentDay: (day) =>
    set((state) => ({
      currentDay: typeof day === 'function' ? day(state.currentDay) : day,
    })),

  showOrbits: true,
  setShowOrbits: (show) => set({ showOrbits: show }),

  showLabels: true,
  setShowLabels: (show) => set({ showLabels: show }),

  cameraTarget: null,
  setCameraTarget: (target) => set({ cameraTarget: target }),
  cameraLookAt: null,
  setCameraLookAt: (target) => set({ cameraLookAt: target }),
  setCameraFocus: (target, lookAt) => set({ cameraTarget: target, cameraLookAt: lookAt }),
  isCameraAnimating: false,
  setCameraAnimating: (v) => set({ isCameraAnimating: v }),

  showKnowledge: false,
  setShowKnowledge: (show) => set({ showKnowledge: show }),

  planetScale: 3,
  setPlanetScale: (scale) => set({ planetScale: Math.max(1, Math.min(10, scale)) }),

  resetView: () =>
    set({
      selectedBody: null,
      viewMode: 'overview',
      cameraTarget: null,
      cameraLookAt: null,
      showKnowledge: false,
      planetScale: 3,
      timeSpeed: '1x',
      currentDay: 0,
      timeMode: 'simulation',
      showOrbits: true,
      showLabels: true,
      journeyMode: 'idle',
      currentJourneyIndex: 0,
      showJourneyHUD: false,
      isCameraAnimating: false,
    }),

  // Achievement system
  unlockedAchievements: getInitialUnlockedAchievements(),
  unlockAchievement: (id) =>
    set((state) => {
      if (state.unlockedAchievements.includes(id)) {
        return state;
      }
      const updated = [...state.unlockedAchievements, id];
      try {
        localStorage.setItem('unlockedAchievements', JSON.stringify(updated));
      } catch {}
      return {
        unlockedAchievements: updated,
        achievementQueue: [...state.achievementQueue, id],
      };
    }),
  achievementQueue: [],
  dequeueAchievement: () =>
    set((state) => {
      if (state.achievementQueue.length === 0) return state;
      return { achievementQueue: state.achievementQueue.slice(1) };
    }),
  showAchievementPanel: false,
  setShowAchievementPanel: (show) => set({ showAchievementPanel: show }),

  // Exploration tracking
  exploredBodies: [],
  addExploredBody: (bodyId) =>
    set((state) => {
      if (state.exploredBodies.includes(bodyId)) {
        return {};
      }
      return {
        exploredBodies: [...state.exploredBodies, bodyId],
      };
    }),

  // Mission counters
  completedMissionCount: 0,
  incrementMissionCount: () =>
    set((state) => ({
      completedMissionCount: state.completedMissionCount + 1,
    })),

  // Knowledge counters
  unlockedKnowledgeCount: getInitialKnowledgeCount(),
  incrementKnowledgeCount: (level) =>
    set((state) => ({
      unlockedKnowledgeCount: {
        ...state.unlockedKnowledgeCount,
        [level]: state.unlockedKnowledgeCount[level] + 1,
      },
    })),

  // Time tracking
  totalTimeAdvanced: 0,
  addTimeAdvanced: (days) =>
    set((state) => ({
      totalTimeAdvanced: state.totalTimeAdvanced + Math.max(0, days),
    })),

  // Journey mode
  journeyMode: 'idle',
  setJourneyMode: (mode) => set({ journeyMode: mode }),
  currentJourneyIndex: 0,
  setCurrentJourneyIndex: (index) => set({ currentJourneyIndex: index }),
  showJourneyHUD: false,
  setShowJourneyHUD: (show) => set({ showJourneyHUD: show }),

  // Prediction game
  showPredictionGame: false,
  setShowPredictionGame: (show) => set({ showPredictionGame: show }),
  predictionBodyId: null,
  setPredictionBodyId: (id) => set({ predictionBodyId: id, predictionResult: null }),
  predictionDays: 100,
  setPredictionDays: (days) => set({ predictionDays: days }),
  predictionUserAngle: 0,
  setPredictionUserAngle: (angle) => set({ predictionUserAngle: angle }),
  predictionResult: null,
  setPredictionResult: (result) => set({ predictionResult: result }),

  // Sandbox experiment
  showSandbox: false,
  setShowSandbox: (show) => set({ showSandbox: show }),
  sandboxOrbitAU: 1.0,
  setSandboxOrbitAU: (au) => set({ sandboxOrbitAU: au }),

  // Mission system
  activeMissionIds: [],
  missionProgressMap: {},
  hintIndexMap: {},
  addActiveMission: (id) =>
    set((state) => {
      if (state.activeMissionIds.length >= MAX_ACTIVE_MISSIONS) return state;
      if (state.activeMissionIds.includes(id)) return state;
      return {
        activeMissionIds: [...state.activeMissionIds, id],
        missionProgressMap: {
          ...state.missionProgressMap,
          [id]: { exploredBodiesInMission: [], compareBodies: [], observedEvents: [] },
        },
        hintIndexMap: { ...state.hintIndexMap, [id]: 0 },
      };
    }),
  removeActiveMission: (id) =>
    set((state) => {
      if (!state.activeMissionIds.includes(id)) return state;
      const next = state.activeMissionIds.filter((mid) => mid !== id);
      const newMap = { ...state.missionProgressMap };
      delete newMap[id];
      const newHints = { ...state.hintIndexMap };
      delete newHints[id];
      return {
        activeMissionIds: next,
        missionProgressMap: newMap,
        hintIndexMap: newHints,
      };
    }),
  addMissionExploredBody: (missionId, bodyId) =>
    set((state) => {
      const progress = state.missionProgressMap[missionId];
      if (!progress) return {};
      if (progress.exploredBodiesInMission.includes(bodyId)) return {};
      return {
        missionProgressMap: {
          ...state.missionProgressMap,
          [missionId]: {
            ...progress,
            exploredBodiesInMission: [...progress.exploredBodiesInMission, bodyId],
          },
        },
      };
    }),
  addMissionCompareBody: (missionId, bodyId) =>
    set((state) => {
      const progress = state.missionProgressMap[missionId];
      if (!progress) return {};
      if (progress.compareBodies.includes(bodyId)) return {};
      return {
        missionProgressMap: {
          ...state.missionProgressMap,
          [missionId]: {
            ...progress,
            compareBodies: [...progress.compareBodies, bodyId],
          },
        },
      };
    }),
  addMissionObservedEvent: (missionId, eventId) =>
    set((state) => {
      const progress = state.missionProgressMap[missionId];
      if (!progress) return {};
      if (progress.observedEvents.includes(eventId)) return {};
      return {
        missionProgressMap: {
          ...state.missionProgressMap,
          [missionId]: {
            ...progress,
            observedEvents: [...progress.observedEvents, eventId],
          },
        },
      };
    }),
  completedMissions: [],
  completeMission: (id) =>
    set((state) => {
      if (state.completedMissions.includes(id)) return state;
      // 清理对应的进度和活跃任务
      const newActiveIds = state.activeMissionIds.filter((mid) => mid !== id);
      const newMap = { ...state.missionProgressMap };
      delete newMap[id];
      const newHints = { ...state.hintIndexMap };
      delete newHints[id];
      return {
        completedMissions: [...state.completedMissions, id],
        activeMissionIds: newActiveIds,
        missionProgressMap: newMap,
        hintIndexMap: newHints,
      };
    }),
  showSpacecraftPanel: false,
  setShowSpacecraftPanel: (show) => set({ showSpacecraftPanel: show }),
  selectedSpacecraft: null,
  setSelectedSpacecraft: (id) => set({ selectedSpacecraft: id }),

  showHohmannDesigner: false,
  setShowHohmannDesigner: (show) => set({ showHohmannDesigner: show }),
  hohmannTarget: 'mars',
  setHohmannTarget: (id) => set({ hohmannTarget: id }),

  showEclipseLab: false,
  setShowEclipseLab: (show) => set({ showEclipseLab: show }),

  showBlackHole: false,
  setShowBlackHole: (show) => set({ showBlackHole: show }),

  showMissionPanel: false,
  setShowMissionPanel: (show) => set({ showMissionPanel: show }),
  nextHint: (missionId) =>
    set((state) => {
      const idx = state.hintIndexMap[missionId] ?? 0;
      return {
        hintIndexMap: { ...state.hintIndexMap, [missionId]: Math.min(idx + 1, 999) },
      };
    }),

  // Voting
  userVotes: {},
  setUserVote: (controversyId, optionId) =>
    set((state) => ({
      userVotes: {
        ...state.userVotes,
        [controversyId]: optionId,
      },
    })),

  // Narrative missions
  showNarrative: false,
  setShowNarrative: (show) => set({ showNarrative: show }),
  activeNarrative: null,
  setActiveNarrative: (id) => set({ activeNarrative: id, narrativeStep: 0 }),
  narrativeStep: 0,
  setNarrativeStep: (step) => set({ narrativeStep: step }),

  // Scientist gallery
  showScientistGallery: false,
  setShowScientistGallery: (show) => set({ showScientistGallery: show }),

  // Scale ruler
  showScaleRuler: false,
  setShowScaleRuler: (show) => set({ showScaleRuler: show }),

  // Star map
  showStarMap: false,
  setShowStarMap: (show) => set({ showStarMap: show }),

  // Exploration history
  showExplorationHistory: false,
  setShowExplorationHistory: (show) => set({ showExplorationHistory: show, explorationHistorySelectedMilestone: null }),
  explorationHistorySelectedMilestone: null,
  setExplorationHistorySelectedMilestone: (id) => set({ explorationHistorySelectedMilestone: id }),

  // 欢迎页阶段管理
  appPhase: 'loading',
  setAppPhase: (phase) => set({ appPhase: phase }),
  isFirstVisit: true,
  markVisited: () => set({ isFirstVisit: false }),
}));
